/**
 * Owner self-signup. Form → POST /auth/signup-owner. Success screen
 * shows tenant_code prominently so Owner can copy/share with staff.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Save, Copy, Check, UserPlus, Shield, ArrowLeft } from 'lucide-react';
import { apiFetch } from '../lib/api-client';
import { useAuthStore } from '../lib/store';
import { useToast } from '../components/ui/toast';
import type { AuthProfile } from '@payroll/shared';
import { TENANT_CODE_PATTERN } from '@payroll/shared';

interface FormData {
  company_name: string;
  owner_name: string;
  email: string;
  password: string;
  confirm: string;
  phone: string;
  tenant_code: string;
}

export function Signup() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [tenantCode, setTenantCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const { register, handleSubmit, formState: { errors }, watch } = useForm<FormData>({
    defaultValues: { company_name: '', owner_name: '', email: '', password: '', confirm: '', phone: '', tenant_code: '' },
  });

  async function onSubmit(form: FormData) {
    setBusy(true); setError(null);
    try {
      const payload: Record<string, string> = {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        company_name: form.company_name,
        owner_name: form.owner_name,
      };
      if (form.phone) payload.phone = form.phone;
      // Empty vanity field → server auto-generates
      if (form.tenant_code) payload.tenant_code = form.tenant_code.toUpperCase();
      const result = await apiFetch<{ tenant_code: string; jwt: string; profile: AuthProfile }>(
        '/auth/signup-owner',
        {
          method: 'POST',
          body: payload,
          skipAuth: true,
        },
      );
      setAuth(result.jwt, result.profile);
      setTenantCode(result.tenant_code);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'สมัครไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }

  async function handleCopy() {
    if (!tenantCode) return;
    try {
      await navigator.clipboard.writeText(tenantCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('คัดลอกไม่สำเร็จ — กรุณาจดด้วยตัวเอง');
    }
  }

  // === Success screen ===
  if (tenantCode) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-amber-50 to-yellow-50">
        <div className="card max-w-sm w-full p-6 text-center space-y-4">
          <div className="text-5xl">⏳</div>
          <h2 className="text-lg font-bold">ส่งใบสมัครเรียบร้อย</h2>
          <p className="text-sm text-gray-600">
            <strong>{watch('company_name')}</strong>
            <br />
            <span className="text-amber-700">รอทีมงานอนุมัติ</span>
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-left">
            <p className="text-sm font-medium mb-1">📌 ขั้นตอนถัดไป</p>
            <p className="text-xs text-gray-700">
              ทีมงาน Payroll Edge กำลังตรวจสอบข้อมูล ปกติไม่เกิน 1 วันทำการ —
              เมื่ออนุมัติแล้วระบบจะปลดล็อกอัตโนมัติ
            </p>
          </div>

          <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
            <div className="text-xs text-gray-600 mb-1">รหัสบริษัทของคุณ</div>
            <div className="text-3xl font-mono font-bold tracking-widest text-brand-700">
              {tenantCode}
            </div>
            <div className="text-xs text-gray-500 mt-2">📌 เก็บไว้ใช้แจกพนักงานหลังอนุมัติ</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-left">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-green-700" />
              <span className="text-sm font-medium">ความปลอดภัย</span>
            </div>
            <p className="text-xs text-gray-700">
              ข้อมูลเงินเดือนและบัญชีถูกเข้ารหัสด้วยกุญแจของบริษัทคุณโดยเฉพาะ
            </p>
          </div>

          <button onClick={handleCopy} className="btn-secondary w-full">
            {copied ? <><Check className="w-4 h-4" /> คัดลอกแล้ว</> : <><Copy className="w-4 h-4" /> คัดลอกรหัส</>}
          </button>
          <button onClick={() => navigate('/launcher', { replace: true })} className="btn-primary w-full">
            <UserPlus className="w-4 h-4" /> เข้าสู่ระบบ
          </button>
        </div>
      </div>
    );
  }

  // === Form ===
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-brand-50 to-cyan-50">
      <div className="card max-w-sm w-full p-6 space-y-4">
        <button onClick={() => navigate('/')} className="text-sm text-gray-500 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> ย้อนกลับ
        </button>
        <h2 className="text-xl font-bold text-center">สมัครบริษัทใหม่</h2>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-gray-700">
          🔐 ข้อมูลเงินเดือนและบัญชีจะถูกเข้ารหัสด้วยกุญแจของบริษัทคุณ — ทีมเรามองไม่เห็นค่าจริง
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div>
            <label className="label">ชื่อบริษัท *</label>
            <input
              {...register('company_name', { required: true, minLength: 2, maxLength: 100 })}
              className="input"
              placeholder="เช่น บริษัท เอบีซี จำกัด"
            />
            {errors.company_name && <p className="text-xs text-red-500 mt-1">กรุณากรอกชื่อบริษัท (2 ตัวขึ้นไป)</p>}
          </div>
          <div>
            <label className="label">ชื่อ-นามสกุลของคุณ *</label>
            <input
              {...register('owner_name', { required: true, minLength: 2, maxLength: 100 })}
              className="input"
            />
            {errors.owner_name && <p className="text-xs text-red-500 mt-1">กรุณากรอกชื่อ</p>}
          </div>
          <div>
            <label className="label">อีเมล * (ใช้เข้าสู่ระบบ)</label>
            <input
              type="email"
              inputMode="email"
              autoCapitalize="none"
              {...register('email', {
                required: true,
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'รูปแบบอีเมลไม่ถูกต้อง' },
              })}
              className="input"
              placeholder="you@company.com"
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message ?? 'กรุณากรอกอีเมล'}</p>}
          </div>
          <div>
            <label className="label">รหัสผ่าน * (อย่างน้อย 8 ตัว)</label>
            <input
              type="password"
              {...register('password', { required: true, minLength: 8 })}
              className="input"
              placeholder="••••••••"
              autoComplete="new-password"
            />
            {errors.password && <p className="text-xs text-red-500 mt-1">รหัสผ่านอย่างน้อย 8 ตัวอักษร</p>}
          </div>
          <div>
            <label className="label">ยืนยันรหัสผ่าน *</label>
            <input
              type="password"
              {...register('confirm', {
                required: true,
                validate: (v) => v === watch('password') || 'รหัสผ่านยืนยันไม่ตรงกัน',
              })}
              className="input"
              placeholder="••••••••"
              autoComplete="new-password"
            />
            {errors.confirm && <p className="text-xs text-red-500 mt-1">{errors.confirm.message ?? 'กรุณายืนยันรหัสผ่าน'}</p>}
          </div>
          <div>
            <label className="label">เบอร์โทร (ไม่บังคับ)</label>
            <input
              type="tel"
              inputMode="tel"
              {...register('phone', { maxLength: 20 })}
              className="input"
              placeholder="0812345678"
            />
          </div>
          <div>
            <label className="label">รหัสบริษัทที่อยากได้ (ไม่บังคับ)</label>
            <input
              {...register('tenant_code', {
                setValueAs: (v: string) => v?.toUpperCase() ?? '',
                pattern: {
                  value: TENANT_CODE_PATTERN,
                  message: 'A-Z / 0-9 ยาว 4-12 ตัว',
                },
              })}
              maxLength={12}
              className="input font-mono tracking-widest uppercase"
              placeholder="ACME01"
              autoCapitalize="characters"
            />
            <p className="text-xs text-gray-500 mt-1">
              {errors.tenant_code?.message ?? 'เว้นว่างเพื่อให้ระบบสุ่มให้ (เช่น STHKJU)'}
            </p>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button type="submit" disabled={busy} className="btn-primary w-full">
            <Save className="w-4 h-4" /> {busy ? 'กำลังสมัคร…' : 'สมัครเลย'}
          </button>
        </form>
      </div>
    </div>
  );
}
